import React, { PureComponent, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { CompanyCodePropTypes } from '../../shared/propTypes/companyCodes';
import { PeriodPropTypes } from '../../shared/propTypes/period';
import { OptionsPropType } from '../../shared/propTypes/common';


import Modal from '../../shared/modals/modal.component';

import FormikProvider from '../../shared/forms/formikProvider.component';
import Form from '../../shared/forms/form.component';
import WarningDialog from '../../shared/warningDialog.component';
import { useDialogState } from '../../shared/hooks/useDialogState';
import { YES_FLAG } from '../../shared/constants';

import ConfirmationDialogContent from './confirmationDialogContent.component';
import schema from './companyCodeForm.schema';

import {
  fetchInitialDataForCompanyCodeModal,
} from './store/actions';

import {
  enabledFlagOptionsSelector,
  yesNoOptionsSelector,
  legalEntityOptionsSelector,
  businessUnitOptionsSelector,
  businessCodeOptionsSelector,
  countryCodeOptionsSelector,
  chartNameOptionsSelector,
  connectionNameOptionsSelector,
  usShareholderFlagOptionsSelector,
  homeOfficeCompanyCodeOptionsSelector,
  currencyCodeOptionsSelector,
  fetchingDataForModalSelector,
  sourceOptionsSelector,
  sharedSelectors,
  companyCodesOptionsToValidateSelector,
} from './store/selectors';

import { bsmFlagSelector } from '../../shared/store/selectors';


const ModalTitles = {
  ADD: 'Add Company Code',
  EDIT: 'Edit Company Code',
};

class AddCompanyCodeModal extends PureComponent {
  static propTypes = {
    fetchInitialDataForCompanyCodeModal: PropTypes.func.isRequired,
    fetchingDataForModal: PropTypes.bool.isRequired,
    hideModal: PropTypes.func.isRequired,
    saveEntity: PropTypes.func.isRequired,
    enabledFlagOptions: OptionsPropType,
    yesNoOptions: OptionsPropType,
    legalEntityOptions: OptionsPropType,
    businessUnitOptions: OptionsPropType,
    businessCodeOptions: OptionsPropType,
    countryCodeOptions: OptionsPropType,
    chartNameOptions: OptionsPropType,
    connectionNameOptions: OptionsPropType,
    currencyCodeOptions: OptionsPropType,
    usShareholderFlagOptions: OptionsPropType,
    homeOfficeCompanyCodeOptions: OptionsPropType,
    sourceOptions: OptionsPropType,
    period: PeriodPropTypes.isRequired,
    isSavingInProgress: PropTypes.bool.isRequired,
    entityToEdit: CompanyCodePropTypes,
    openForEdit: PropTypes.bool,
    isVisible: PropTypes.bool,
    companyCodesOptionsToValidate: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })),
    clientBsmFlag: PropTypes.bool,
  };

  state = {
    addAnother: false,
  };

  componentDidUpdate(prevProps) {
    const { isVisible, fetchInitialDataForCompanyCodeModal, period, entityToEdit } = this.props;
    if (!prevProps.isVisible && isVisible) {
      fetchInitialDataForCompanyCodeModal({ period, entityToEdit });
    }
  }

  saveCompanyCode = (companyCodeData, { setSubmitting, resetForm }) => {
    const { period, saveEntity, openForEdit, entityToEdit } = this.props;
    const { addAnother } = this.state;

    this.setState({ addAnother: false }, () => {
      saveEntity({
        ...companyCodeData,
        periodName: period.name,
        chartNameOriginal: openForEdit ? entityToEdit.chartName : null,
      }, openForEdit, { callback: resetForm, keepOpenAfterSaving: addAnother });
      setSubmitting(false);
    });
  };

  renderModal = ({ formik, schema }) => {
    const {
      enabledFlagOptions,
      yesNoOptions,
      legalEntityOptions,
      businessUnitOptions,
      businessCodeOptions,
      countryCodeOptions,
      chartNameOptions,
      connectionNameOptions,
      currencyCodeOptions,
      usShareholderFlagOptions,
      homeOfficeCompanyCodeOptions,
      sourceOptions,
      isSavingInProgress,
      openForEdit,
      hideModal,
      fetchingDataForModal,
      isVisible,
      clientBsmFlag,
    } = this.props;

    const {
      showDialog: showSaveDialog,
      hideDialog: hideSaveDialog,
      isDialogVisible: isSaveDialogVisible,
    } = useDialogState();

    const submit = useCallback(() => {
      const shouldShowWarning = openForEdit
        && formik.values.subledgerFlag === YES_FLAG
        && formik.initialValues.overrideTaxCurrency !== formik.values.overrideTaxCurrency;

      this.setState({
        addAnother: false,
      }, () => {
        if (shouldShowWarning) {
          showSaveDialog();
        } else {
          formik.handleSubmit();
        }
      });
    }, [formik, showSaveDialog, openForEdit]);

    const submitAndAddAnotherAction = useCallback(() => {
      const shouldShowWarning = openForEdit
        && formik.values.subledgerFlag === YES_FLAG
        && formik.touched.overrideTaxCurrency;

      this.setState({
        addAnother: true,
      }, () => {
        if (shouldShowWarning) {
          showSaveDialog();
        } else {
          formik.handleSubmit();
        }
      });
    }, [formik, showSaveDialog, openForEdit]);

    const reset = useCallback(() => {
      hideModal();
      formik.resetForm();
    }, [formik, hideModal]);

    const confirmSave = useCallback(() => {
      hideSaveDialog();
      formik.handleSubmit();
    }, [formik, hideSaveDialog]);

    const confirmCancel = useCallback(() => {
      hideSaveDialog();
    }, [hideSaveDialog]);

    return (
      <>
        <Modal
          className="add-company-code-modal"
          title={openForEdit ? ModalTitles.EDIT : ModalTitles.ADD}
          closeAction={reset}
          dismissText="Cancel"
          dismissAction={reset}
          submitText="Save"
          submitAction={submit}
          isLoading={fetchingDataForModal}
          isSubmitting={formik.isSubmitting || isSavingInProgress}
          submitDisabled={fetchingDataForModal}
          submitAndAddAnotherAction={!openForEdit ? submitAndAddAnotherAction : null}
          submitAndAddAnotherText="Save and Add Another"
          isVisible={isVisible}
        >
          <Form
            formik={formik}
            schema={schema}
            openForEdit={openForEdit}
            formContext={{
              enabledFlagOptions,
              yesNoOptions,
              legalEntityOptions,
              businessUnitOptions,
              businessCodeOptions,
              countryCodeOptions,
              chartNameOptions,
              connectionNameOptions,
              currencyCodeOptions,
              usShareholderFlagOptions,
              homeOfficeCompanyCodeOptions,
              sourceOptions,
              openForEdit,
              clientBsmFlag,
            }}
          />
        </Modal>
        <WarningDialog
          onCancel={confirmCancel}
          onConfirm={confirmSave}
          message={<ConfirmationDialogContent />}
          title="Warning!"
          dismissText="Cancel"
          submitText="Save"
          isVisible={isSaveDialogVisible}
        />
      </>
    );
  };

  render() {
    const {
      entityToEdit,
      legalEntityOptions,
      businessUnitOptions,
      businessCodeOptions,
      countryCodeOptions,
      chartNameOptions,
      connectionNameOptions,
      currencyCodeOptions,
      usShareholderFlagOptions,
      homeOfficeCompanyCodeOptions,
      sourceOptions,
      companyCodesOptionsToValidate,
      openForEdit,
      clientBsmFlag,
    } = this.props;

    return (
      <FormikProvider
        schema={schema}
        entityToEdit={entityToEdit}
        onSubmit={this.saveCompanyCode}
        render={this.renderModal}
        validationContext={{
          legalEntityOptions,
          businessUnitOptions,
          businessCodeOptions,
          countryCodeOptions,
          chartNameOptions,
          connectionNameOptions,
          currencyCodeOptions,
          usShareholderFlagOptions,
          homeOfficeCompanyCodeOptions,
          sourceOptions,
          companyCodesOptionsToValidate,
          openForEdit,
          clientBsmFlag,
        }}
      />
    );
  }
}

function mapStateToProps(state) {
  return {
    enabledFlagOptions: enabledFlagOptionsSelector(state),
    yesNoOptions: yesNoOptionsSelector(state),
    legalEntityOptions: legalEntityOptionsSelector(state),
    businessUnitOptions: businessUnitOptionsSelector(state),
    businessCodeOptions: businessCodeOptionsSelector(state),
    countryCodeOptions: countryCodeOptionsSelector(state),
    chartNameOptions: chartNameOptionsSelector(state),
    connectionNameOptions: connectionNameOptionsSelector(state),
    currencyCodeOptions: currencyCodeOptionsSelector(state),
    usShareholderFlagOptions: usShareholderFlagOptionsSelector(state),
    homeOfficeCompanyCodeOptions: homeOfficeCompanyCodeOptionsSelector(state),
    sourceOptions: sourceOptionsSelector(state),
    fetchingDataForModal: fetchingDataForModalSelector(state),
    isSavingInProgress: sharedSelectors.savingEntitySelector(state),
    companyCodesOptionsToValidate: companyCodesOptionsToValidateSelector(state),
    clientBsmFlag: bsmFlagSelector(state),
  };
}

export default connect(
  mapStateToProps,
  {
    fetchInitialDataForCompanyCodeModal,
  }
)(AddCompanyCodeModal);

export const PureAddCompanyCodeModal = AddCompanyCodeModal;
